import React, { useState } from 'react';
import DynamicInputList from '../software-dynamic-version-7/list-input-v-7';
import securityService from '../security/security.service';
import setupService from '../../services/setup.service';
import BearerTokenAdd from './bearer-token-add';
import bearerTokenService from './bearer-token.service';
import './bearer-token.scss';

// ─── Password Gate Screen ────────────────────────────────────────────────────
const TokenPasswordGate = ({ onSuccess }) => {
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const handleVerify = async () => {
    const trimmed = password.trim();
    if (!trimmed) {
      setError('Please enter the Token Password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await setupService.getAll('Company');
      const companies = result?.data?.recordset ?? [];

      // Match entered password against any company's TokenPassword
      const isValid = companies.some(
        (company) => company.TokenPassword === trimmed
      );

      if (isValid) {
        onSuccess();
      } else {
        setError('Incorrect Token Password. Please try again.');
      }
    } catch (err) {
      console.error('Token verification error:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="#009f60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <h2 style={styles.title}>Verify Token Password</h2>
        <p style={styles.subtitle}>
          Enter the Token Password to access Bearer Tokens.
        </p>

        <input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          placeholder="Enter Token Password"
          style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
          autoFocus
          disabled={loading}
        />

        {error && <p style={styles.errorText}>{error}</p>}

        <button
          onClick={handleVerify}
          disabled={loading}
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
        >
          {loading ? 'Verifying…' : 'Verify & Continue'}
        </button>
      </div>
    </div>
  );
};

// ─── Inline Styles ────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '85vh',
    background: 'transparent',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
  },
  iconWrap: {
    background: '#eef1ff',
    borderRadius: '50%',
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '4px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a2e',
    textAlign: 'center',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '15px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: '#ef4444',
    textAlign: 'center',
  },
  button: {
    width: '100%',
    padding: '11px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#fff',
    background: '#009f60',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginTop: '4px',
  },
  buttonDisabled: {
    background: '#a5b4fc',
    cursor: 'not-allowed',
  },
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BearerTokenList = (props) => {
  const [verified, setVerified] = useState(false);

  // Show password gate until verified
  if (!verified) {
    return <TokenPasswordGate onSuccess={() => setVerified(true)} />;
  }

  // ── Grid config (unchanged from original) ──────────────────────────────────
  const columns = [
    { field: 'CompanyID',             headerText: 'ID',           width: '120px', textAlign: 'right', headerTextAlign: 'center', other: [{}] },
    { field: 'CompanyName',           headerText: 'Company Name', width: '200px', textAlign: 'left',  headerTextAlign: 'center', other: [{}] },
    { field: 'CompanyNTNCNIC',        headerText: 'NTN / CNIC',   width: '160px', textAlign: 'left',  headerTextAlign: 'center', other: [{}] },
    { field: 'BearerToken',           headerText: 'Bearer Token', width: '220px', textAlign: 'left',  headerTextAlign: 'center', other: [{}] },
    { field: 'BearerTokenForTesting', headerText: 'Test Token',   width: '220px', textAlign: 'left',  headerTextAlign: 'center', other: [{}] },
  ];

  const getList = async () => {
    return await setupService.getAll('Company');
  };

  const listActionComplete = async (grid, args, getListService) => {
    if (args.requestType === 'save' || args.requestType === 'cancel') {
      getListService();
    }

    if (args.requestType === 'save') {
      const data = {
        CompanyName:           args.data.CompanyName,
        CompanyNTNCNIC:        args.data.CompanyNTNCNIC,
        BearerToken:           args.data.BearerToken,
        BearerTokenForTesting: args.data.BearerTokenForTesting,
        CompanyID:             args.data.CompanyID || securityService.getCompanyIDLoggedInUserId(),
      };

      if (args.action === 'edit' && data.CompanyID) {
        await bearerTokenService.update(data.CompanyID, data);
        await getListService();
      }
    }
  };

  return (
    <DynamicInputList
      Id='BearerToken'
      ListTitle={`${props?.Title || 'Bearer Token'}  -  List`}
      ColumnsList={columns}
      GridDataSource={[{ DataSourceType: 'Function', DataSource: () => getList() }]}
      DefaultSortedColumnName='CompanyID'
      GridDialog={[{
        DialogType: 'Self',
        Dialog: (
          <BearerTokenAdd
            UserID={securityService.getLoggedInUserId()}
            Title={`${props?.Title || 'Bearer Token'}`}
            {...props}
          />
        ),
      }]}
      ListActionComplete={listActionComplete}
      ToolbarClicking={async () => {}}
      AllowTemplate={false}
      ToolbarOption={['Edit', 'Update', 'Cancel', 'PDFExport', 'ExcelExport', 'Search', 'ColumnChooser']}
      ListSecurity={true}
      EditSecurity={true}
      AllowGridSum={false}
      GridSumColumns={[]}
      HeadTitle={`${props?.Title || 'Bearer Token'}  -  List`}
      IsDialog={props?.IsDialog ?? props?.isDialog ?? false}
      {...props}
    />
  );
};

export default BearerTokenList;